import { ReferenceDataItem } from "@coherehealth/qm-api";
import { getReviewCaseOutcomeMapping } from "../../util/TableUtil";

// Manual replica of switch cases in faxIntakeCaseOutcomeMapping (TableUtil.tsx).
// That function is NOT exported, so we can't call it programmatically.
// MAINTENANCE NOTE: if a new case is added to faxIntakeCaseOutcomeMapping in TableUtil.tsx,
// add it here too to keep the search filter in sync.
const FAX_OUTCOME_IDS = new Set([
  "NEW_SR",
  "NEW_CONTINUATION_SR",
  "FAX_ATTACHED",
  "FAX_ATTACHED_TO_RF",
  "FAXBACK",
  "FORWARDED",
  "SPLIT_FAX",
  "EDIT_EXISTING_SR",
  "NEW_REFERRAL",
  "HEALTH_HELP",
  "CLAIM",
  "ANOTHER_SYSTEM",
]);

// All ReviewOutcome values; derived from the existing exported function so the two stay in sync.
const REVIEW_OUTCOME_IDS = new Set(Object.keys(getReviewCaseOutcomeMapping()));

// Confirmed from DenialPreviewModal.tsx + caseCompletePageUtils.ts (letter writing completion paths).
// CONCURRENT_LETTER_WRITING is intentionally absent — no known outcome-writing path found; falls back to full list (AC4).
const LETTER_WRITING_OUTCOME_IDS = new Set(["LETTER_QUEUED_FOR_DELIVERY", "UNASSIGNED_HOLD_IN_QUEUE", "LETTER_SAVED"]);

/**
 * Maps each case type to the set of valid outcome IDs for that type.
 * A missing key means "no known mapping" : callers must fall back to the full list (AC4).
 */
export const CASE_TYPE_OUTCOME_IDS: Partial<Record<string, Set<string>>> = {
  FAX_INTAKE: FAX_OUTCOME_IDS,
  LETTER_WRITING: LETTER_WRITING_OUTCOME_IDS,
  RN_REVIEW: REVIEW_OUTCOME_IDS,
  CPP_REVIEW: REVIEW_OUTCOME_IDS,
  ADMIN_REVIEW: REVIEW_OUTCOME_IDS,
  MD_REVIEW: REVIEW_OUTCOME_IDS,
  P2P_REVIEW: REVIEW_OUTCOME_IDS,
  OUT_OF_NETWORK_REVIEW: REVIEW_OUTCOME_IDS,
  OUT_OF_SCOPE_AUDIT_REVIEW: REVIEW_OUTCOME_IDS,
  MISSING_INFORMATION: REVIEW_OUTCOME_IDS,
  NUDGE_OUTREACH: REVIEW_OUTCOME_IDS,
  SCHEDULING_OUTREACH: REVIEW_OUTCOME_IDS,
};

/**
 * Deduplicates a ReferenceDataItem list by value, keeping the FIRST occurrence of each value.
 * Needed because the BE getOutcomes() combines ReviewOutcome + Outcome enums; both define
 * OUT_OF_SCOPE, so the response contains two items with value="OUT_OF_SCOPE". Keeping the
 * first occurrence preserves ReviewOutcome.OUT_OF_SCOPE ("Pending out of scope audit") over
 * Outcome.OUT_OF_SCOPE ("Out of scope") — ReviewOutcome values come first in the BE response
 * and the label matches what the results table shows for review cases with that outcome.
 * Items with undefined value are treated as sharing the key ""; first one wins there too.
 */
export function deduplicateByValue(items: ReferenceDataItem[]): ReferenceDataItem[] {
  const seen = new Set<string>();
  return items.filter((o) => {
    const v = o.value ?? "";
    if (seen.has(v)) {
      return false;
    }
    seen.add(v);
    return true;
  });
}

/**
 * Pure function: filters a full outcome list to only the outcomes valid for the given case types.
 * Returns the same `allOutcomes` reference (no copy) when no filtering is needed, so the
 * useMemo in FindACase.tsx gets a stable reference and doesn't trigger unnecessary re-renders.
 */
export function filterOutcomesByCaseTypes(
  allOutcomes: ReferenceDataItem[],
  selectedCaseTypeIds: string[]
): ReferenceDataItem[] {
  if (!allOutcomes.length || !selectedCaseTypeIds.length) {
    return allOutcomes;
  }
  // Any type with no map entry triggers full list fallback (AC4).
  if (selectedCaseTypeIds.some((id) => !CASE_TYPE_OUTCOME_IDS[id])) {
    return allOutcomes;
  }
  // Union of valid outcome IDs across all selected types.
  const validIds = new Set(selectedCaseTypeIds.flatMap((id) => Array.from(CASE_TYPE_OUTCOME_IDS[id] ?? [])));
  return allOutcomes.filter((o) => validIds.has(o.value ?? ""));
}
